import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface CartItem {
  id: string;
  product_id: string;
  qty: number;
  product?: {
    id: string;
    name: string;
    list_price: number;
    currency: string;
    image_url?: string;
    unit?: string;
  };
}

interface ShopCartContextType {
  items: CartItem[];
  loading: boolean;
  cartId: string | null;
  itemCount: number;
  total: number;
  currency: string;
  add: (productId: string, qty?: number) => Promise<void>;
  setQty: (productId: string, qty: number) => Promise<void>;
  remove: (productId: string) => Promise<void>;
  clear: () => Promise<void>;
  refresh: () => Promise<void>;
}

const ShopCartContext = createContext<ShopCartContextType | null>(null);

export function ShopCartProvider({ children }: { children: ReactNode }) {
  const { user, company } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [cartId, setCartId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCart = useCallback(async () => {
    if (!user?.id || !company?.id) {
      setItems([]);
      setCartId(null);
      setLoading(false);
      return;
    }

    try {
      // Get or create active cart
      let { data: cart } = await supabase
        .from("shop_carts")
        .select("id")
        .eq("company_id", company.id)
        .eq("user_id", user.id)
        .eq("status", "active")
        .maybeSingle();

      if (!cart) {
        const { data: newCart } = await supabase
          .from("shop_carts")
          .insert({
            company_id: company.id,
            user_id: user.id,
            status: "active",
          })
          .select("id")
          .single();
        cart = newCart;
      }

      if (!cart) {
        setLoading(false);
        return;
      }

      setCartId(cart.id);

      // Load cart items with product details
      const { data: cartItems } = await supabase
        .from("shop_cart_items")
        .select(`
          id,
          product_id,
          qty,
          shop_products (
            id,
            name,
            list_price,
            currency,
            image_url,
            unit
          )
        `)
        .eq("cart_id", cart.id);

      const mapped = (cartItems || []).map((item: any) => ({
        id: item.id,
        product_id: item.product_id,
        qty: item.qty,
        product: item.shop_products,
      }));

      setItems(mapped);
    } catch (error) {
      console.error("Error loading cart:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, company?.id]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const add = useCallback(async (productId: string, qty = 1) => {
    if (!cartId) return;

    const existingItem = items.find((i) => i.product_id === productId);

    if (existingItem) {
      await supabase
        .from("shop_cart_items")
        .update({ qty: existingItem.qty + qty })
        .eq("id", existingItem.id);
    } else {
      await supabase.from("shop_cart_items").insert({
        cart_id: cartId,
        product_id: productId,
        qty,
      });
    }

    await loadCart();
  }, [cartId, items, loadCart]);

  const setQty = useCallback(async (productId: string, qty: number) => {
    if (!cartId) return;

    const item = items.find((i) => i.product_id === productId);
    if (!item) return;

    if (qty <= 0) {
      await supabase.from("shop_cart_items").delete().eq("id", item.id);
    } else {
      await supabase
        .from("shop_cart_items")
        .update({ qty })
        .eq("id", item.id);
    }

    await loadCart();
  }, [cartId, items, loadCart]);

  const remove = useCallback(async (productId: string) => {
    await setQty(productId, 0);
  }, [setQty]);

  const clear = useCallback(async () => {
    if (!cartId) return;
    await supabase.from("shop_cart_items").delete().eq("cart_id", cartId);
    await loadCart();
  }, [cartId, loadCart]);

  const itemCount = items.reduce((sum, i) => sum + i.qty, 0);
  const total = items.reduce((sum, i) => sum + (i.product?.list_price || 0) * i.qty, 0);
  const currency = items[0]?.product?.currency || "EUR";

  return (
    <ShopCartContext.Provider
      value={{
        items,
        loading,
        cartId,
        itemCount,
        total,
        currency,
        add,
        setQty,
        remove,
        clear,
        refresh: loadCart,
      }}
    >
      {children}
    </ShopCartContext.Provider>
  );
}

export function useShopCart() {
  const context = useContext(ShopCartContext);
  if (!context) {
    throw new Error("useShopCart must be used within ShopCartProvider");
  }
  return context;
}
